import { gql } from "@apollo/client";
import CiroTableCell, {
  CiroTableCellAlignEnum,
} from "../../shared/CiroTable/CiroTableCell";
import CiroTableRow from "../../shared/CiroTable/CiroTableRow";
import classNames from "classnames";
import {
  AutopilotSessionContactRow_AutopilotCuratedSearchFragment,
  AutopilotSessionContactRow_ContactExtraDetailsFragment,
  AutopilotSessionContactRow_ContactFragment,
  CiroOrgContactCellInfo_OrgContactFragmentDoc,
} from "../../../__generated__/graphql";
import CiroCheckbox from "../../shared/CiroCheckbox";
import AutomationListFindEmailsInlineButton, {
  AutomationListFindEmailsInlineButton_OrgContact,
} from "../shared/AutomationListFindEmailsInlineButton";
import AutomationListFindNumbersInlineButton, {
  AutomationListFindNumbersInlineButton_OrgContact,
} from "../shared/AutomationListFindNumbersInlineButton";
import CopyButton from "../shared/CopyButton";
import { useContactInfoContext } from "../../../reactHooks/autopilotList/useContactInfoContext";
import AutopilotScoringResultTableCell from "../../autopilotCuratedSearch/AutopilotScoringResultTableCell";
import AutopilotCriteriaMatchedCellInfo from "../shared/AutopilotCriteriaMatchedCellInfo";
import LinkedInCompanyCellInfo, {
  LinkedInCompanyCellInfo_LinkedInCompany,
} from "../../shared/CiroTable/LinkedInCompanyCellInfo";
import {
  useFragment as getFragmentData,
  useFragment,
} from "../../../__generated__";
import CiroOrgContactCellInfo, {
  CiroOrgContactCellInfo_OrgContact,
} from "../../shared/CiroTable/CiroOrgContactCellInfo";

export const AutopilotSessionContactRow_Contact = gql`
  fragment AutopilotSessionContactRow_Contact on AutopilotSessionSavedContact {
    id
    org_contact {
      id
      company_name
      orgPhoneNumbers {
        phone_number
      }
      orgEmails {
        email
      }
      mostRecentPhoneNumberRequestTransaction {
        id
        finished_at
      }
      mostRecentEmailRequestTransaction {
        id
        finished_at
      }
      ...CiroOrgContactCellInfo_OrgContact
      ...AutomationListFindEmailsInlineButton_OrgContact
      ...AutomationListFindNumbersInlineButton_OrgContact
    }
    scoringCriteriaAnalysis {
      matched_criteria
      scoring_criterion_id
    }
    corrected_at
  }
  ${AutomationListFindEmailsInlineButton_OrgContact}
  ${AutomationListFindNumbersInlineButton_OrgContact}
  ${CiroOrgContactCellInfo_OrgContact}
`;

export const AutopilotSessionContactRow_AutopilotCuratedSearch = gql`
  fragment AutopilotSessionContactRow_AutopilotCuratedSearch on AutopilotCuratedSearch {
    scoringCriteria {
      id
      label
      criterion
    }
  }
`;

export const AutopilotSessionContactRow_ContactExtraDetails = gql`
  fragment AutopilotSessionContactRow_ContactExtraDetails on AutopilotSessionSavedContact {
    id
    org_contact {
      linkedInCompany {
        ...LinkedInCompanyCellInfo_LinkedInCompany
      }
    }
    scoringCriteriaAnalysis {
      id
      scoring_criterion_id
      matched_criteria
      reason
      sources
    }
  }

  ${LinkedInCompanyCellInfo_LinkedInCompany}
`;

interface IAutopilotSessionContactRowProps {
  autopilotSessionId: number | null;
  extraDetails: AutopilotSessionContactRow_ContactExtraDetailsFragment | null;
  extraDetailsLoading: boolean;
  contact: AutopilotSessionContactRow_ContactFragment;
  curatedSearch:
    | AutopilotSessionContactRow_AutopilotCuratedSearchFragment
    | null
    | undefined;
  isSelected: boolean;
  onSelect: () => void;
  isLastTwoRows: boolean;
}

const AutopilotSessionContactRow = ({
  autopilotSessionId,
  contact,
  extraDetails,
  extraDetailsLoading,
  curatedSearch,
  isSelected,
  onSelect,
}: IAutopilotSessionContactRowProps) => {
  const { getContactInfo } = useContactInfoContext();
  const orgContactId = contact.org_contact?.id;

  /**
   * Getting this phone/email data from a context is an ANTI-PATTERN.
   *
   * Normally we should be getting it from an Apollo fragment. There is some bug
   * we have not been able to solve with updating the Apollo cache for OrgContacts
   * after phoneRequestTransactions and emailRequestTransactions are completed, so
   * we are using a context in this case.
   *
   * Do NOT replicate this pattern in other parts of the codebase.
   */
  const contextData = orgContactId ? getContactInfo(orgContactId) : null;

  const phoneNumber = contextData?.phoneNumbers[0] ?? contact.org_contact?.orgPhoneNumbers[0]?.phone_number;
  const phoneTransaction = contextData?.mostRecentPhoneRequestTransaction ?? contact.org_contact?.mostRecentPhoneNumberRequestTransaction;
  const showPhoneNotFound = !phoneNumber && phoneTransaction?.finished_at;

  const email = contextData?.emails[0] ?? contact.org_contact?.orgEmails[0]?.email;
  const emailTransaction = contextData?.mostRecentEmailRequestTransaction ?? contact.org_contact?.mostRecentEmailRequestTransaction;
  const showEmailNotFound = !email && emailTransaction?.finished_at;

  const cellInfoOrgContact = useFragment(
    CiroOrgContactCellInfo_OrgContactFragmentDoc,
    contact.org_contact,
  );

  const allSources = Array.from(
    new Set(
      extraDetails?.scoringCriteriaAnalysis
        ?.flatMap((result) => result.sources ?? [])
        .filter(Boolean),
    ),
  );

  return (
    <CiroTableRow clickable={false}>
      <CiroTableCell padding={2} minWidth="50px">
        <div className={classNames("pl-2")}>
          <CiroCheckbox checked={isSelected} onClick={onSelect} />
        </div>
      </CiroTableCell>
      <CiroTableCell
        className={classNames("align-top", "whitespace-normal")}
        minWidth="200px"
      >
        <CiroOrgContactCellInfo
          orgContact={cellInfoOrgContact}
          showCompanyName={false}
        />
      </CiroTableCell>
      <CiroTableCell className={classNames("items-center")} minWidth="150px">
        <LinkedInCompanyCellInfo
          linkedInCompany={getFragmentData(
            LinkedInCompanyCellInfo_LinkedInCompany,
            extraDetails?.org_contact?.linkedInCompany,
          )}
          companyName={contact.org_contact?.company_name ?? ""}
        />
      </CiroTableCell>
      <CiroTableCell align={CiroTableCellAlignEnum.CENTER}>
        <AutopilotCriteriaMatchedCellInfo
          scoringCriteria={curatedSearch?.scoringCriteria ?? []}
          scoringCriteriaResults={contact.scoringCriteriaAnalysis ?? []}
          sources={allSources}
        />
      </CiroTableCell>
      {(curatedSearch?.scoringCriteria ?? []).map((scoringCriterion) => {
        const scoringCriteriaExtraDetails =
          extraDetails?.scoringCriteriaAnalysis?.find(
            (result) => result.scoring_criterion_id === scoringCriterion.id,
          );
        const result = contact.scoringCriteriaAnalysis?.find(
          (result) => result.scoring_criterion_id === scoringCriterion.id,
        );

        return (
          <AutopilotScoringResultTableCell
            key={scoringCriterion.id}
            result={{
              criterion_fit: result?.matched_criteria,
              reasoning: scoringCriteriaExtraDetails?.reason,
              sources: scoringCriteriaExtraDetails?.sources,
            }}
            loading={extraDetailsLoading}
            isLast={false}
          />
        );
      })}
      <CiroTableCell minWidth="200px" className={classNames("align-top")}>
        <div className={classNames("flex", "flex-col", "gap-1")}>
          {phoneNumber ? (
            <div className={classNames("flex", "items-center", "gap-2")}>
              <span title={phoneNumber} className={classNames("min-w-32")}>
                {phoneNumber}
              </span>
              <CopyButton
                analyticsField="Copy phone number from autopilot session"
                textToCopy={phoneNumber}
              />
            </div>
          ) : showPhoneNotFound ? (
            <span className={classNames("text-neutral-400")}>
              Phone not found
            </span>
          ) : (
            <AutomationListFindNumbersInlineButton
              autopilotSessionId={autopilotSessionId}
              orgContact={contact.org_contact}
            />
          )}
          {email ? (
            <div className={classNames("flex", "items-center", "gap-2")}>
              <span title={email}>
                {email.length > 30 ? `${email.slice(0, 30)}...` : email}
              </span>
              <CopyButton
                analyticsField="Copy email from autopilot session"
                textToCopy={email}
              />
            </div>
          ) : showEmailNotFound ? (
            <span className={classNames("text-neutral-400")}>
              Email not found
            </span>
          ) : (
            <AutomationListFindEmailsInlineButton
              autopilotSessionId={autopilotSessionId}
              orgContact={contact.org_contact}
            />
          )}
        </div>
      </CiroTableCell>
    </CiroTableRow>
  );
};

export default AutopilotSessionContactRow;
